// ─── Demande de date d'examen à l'auto-école (calculs purs) ─────────────────────────────────────

export type ExamDateRequestStatus = 'pending' | 'accepted' | 'declined' | 'cancelled';

export type ExamDateRequest = {
  id: string;
  status: ExamDateRequestStatus;
  earliestDate: string | null;
  latestDate: string | null;
  note: string | null;
  reply: string | null;
  createdAt: string;
  decidedAt: string | null;
};

/** GET /v1/candidate-portal/me/exam-date-request. */
export type ExamDateRequestView = { canRequest: boolean; request: ExamDateRequest | null };

export type ExamDateDraft = { earliestDate: string; latestDate: string; note: string };

export const EXAM_DATE_NOTE_MAX = 500;
/** Horizon de la période souhaitée (même règle que le serveur). */
export const EXAM_DATE_HORIZON_DAYS = 365;

export const emptyExamDateDraft = (): ExamDateDraft => ({ earliestDate: '', latestDate: '', note: '' });

const STATUSES: ExamDateRequestStatus[] = ['pending', 'accepted', 'declined', 'cancelled'];

/** Réponse du serveur, ou rien à montrer (réponse inattendue) : aucune demande n'est proposée. */
export function examDateRequestOf(data: unknown): ExamDateRequestView | null {
  if (!data || typeof data !== 'object') return null;
  const { canRequest, request } = data as Partial<ExamDateRequestView>;
  if (typeof canRequest !== 'boolean') return null;
  const valid = request && typeof request.id === 'string' && STATUSES.includes(request.status);
  return { canRequest, request: valid ? request : null };
}

/** Jour civil local (AAAA-MM-JJ). */
export function isoDay(date: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

/** Bornes des champs de date : d'aujourd'hui à l'horizon. */
export function dateBounds(today = new Date()) {
  const max = new Date(today);
  max.setDate(max.getDate() + EXAM_DATE_HORIZON_DAYS);
  return { min: isoDay(today), max: isoDay(max) };
}

/** Problème de saisie à signaler avant l'envoi, ou `null`. */
export function draftProblem(draft: ExamDateDraft, today = new Date()): string | null {
  const { min, max } = dateBounds(today);
  for (const day of [draft.earliestDate, draft.latestDate]) {
    if (day && day < min) return 'La période souhaitée ne peut pas commencer avant aujourd’hui.';
    if (day && day > max) return 'Choisissez une période dans les 12 prochains mois.';
  }
  if (draft.earliestDate && draft.latestDate && draft.latestDate < draft.earliestDate)
    return 'La date « au plus tard » doit suivre la date « à partir du ».';
  if (draft.note.trim().length > EXAM_DATE_NOTE_MAX) return `Votre message ne doit pas dépasser ${EXAM_DATE_NOTE_MAX} caractères.`;
  return null;
}

/** Corps envoyé au serveur : champs vides omis. */
export function draftPayload(draft: ExamDateDraft) {
  const note = draft.note.trim();
  return {
    ...(draft.earliestDate ? { earliestDate: draft.earliestDate } : {}),
    ...(draft.latestDate ? { latestDate: draft.latestDate } : {}),
    ...(note ? { note } : {}),
  };
}

const fr = (day: string) => day.split('-').reverse().join('/');

/** Période souhaitée, en clair. */
export function periodLabel(request: Pick<ExamDateRequest, 'earliestDate' | 'latestDate'>): string {
  const { earliestDate: from, latestDate: to } = request;
  if (from && to) return from === to ? `Le ${fr(from)}` : `Du ${fr(from)} au ${fr(to)}`;
  if (from) return `À partir du ${fr(from)}`;
  if (to) return `Au plus tard le ${fr(to)}`;
  return 'Dès que possible';
}
